import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Upload, Trash2, Edit, Download, Search, FileText, FileImage,
  FileSpreadsheet, File as FileIcon, Folder, HardDrive, Clock, Tag, X,
  LayoutGrid, List, Inbox, ChevronRight, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";

// File-type icon + colour map. The bg/text classes shape the colourful chips
// that replace the previous monochrome icons throughout the page.
const FILE_TYPE_META = {
  pdf:  { Icon: FileText,        bg: "bg-rose-50",     text: "text-rose-600",     ring: "ring-rose-100" },
  doc:  { Icon: FileText,        bg: "bg-blue-50",     text: "text-blue-600",     ring: "ring-blue-100" },
  docx: { Icon: FileText,        bg: "bg-blue-50",     text: "text-blue-600",     ring: "ring-blue-100" },
  xls:  { Icon: FileSpreadsheet, bg: "bg-emerald-50",  text: "text-emerald-600",  ring: "ring-emerald-100" },
  xlsx: { Icon: FileSpreadsheet, bg: "bg-emerald-50",  text: "text-emerald-600",  ring: "ring-emerald-100" },
  csv:  { Icon: FileSpreadsheet, bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-100" },
  jpg:  { Icon: FileImage,       bg: "bg-violet-50",   text: "text-violet-600",   ring: "ring-violet-100" },
  jpeg: { Icon: FileImage,       bg: "bg-violet-50",   text: "text-violet-600",   ring: "ring-violet-100" },
  png:  { Icon: FileImage,       bg: "bg-violet-50",   text: "text-violet-600",   ring: "ring-violet-100" },
  webp: { Icon: FileImage,       bg: "bg-violet-50",   text: "text-violet-600",   ring: "ring-violet-100" },
  gif:  { Icon: FileImage,       bg: "bg-pink-50",     text: "text-pink-600",     ring: "ring-pink-100" },
  txt:  { Icon: FileIcon,        bg: "bg-slate-100",   text: "text-slate-600",    ring: "ring-slate-100" },
};
const LINK_META = { Icon: FileIcon, bg: "bg-amber-50", text: "text-amber-600", ring: "ring-amber-100" };
const DEFAULT_META = { Icon: FileIcon, bg: "bg-slate-100", text: "text-slate-500", ring: "ring-slate-100" };

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "webp", "gif"]);

const COLOR_PALETTE = [
  "#002FA7", "#10B981", "#F59E0B", "#8B5CF6",
  "#EC4899", "#EF4444", "#06B6D4", "#64748B"
];

const getFileExt = (filename) => {
  if (!filename) return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getFileIcon = (filename) => {
  const ext = getFileExt(filename);
  return (FILE_TYPE_META[ext] || DEFAULT_META).Icon;
};

const getFileMeta = (doc) => {
  if (doc?.tipo === "link") return LINK_META;
  return FILE_TYPE_META[getFileExt(doc?.original_filename)] || DEFAULT_META;
};

const isImageDoc = (doc) => doc?.tipo === "file" && IMAGE_EXTS.has(getFileExt(doc?.original_filename));
const isPdfDoc = (doc) => doc?.tipo === "file" && getFileExt(doc?.original_filename) === "pdf";

// URL helpers that include the JWT in the query string — needed because
// <img> tags can't send custom Authorization headers.
const getDownloadUrl = (doc) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;
const getThumbnailUrl = (doc) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/thumbnail?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;

const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("es-ES");
};

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("__all__");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("docs.viewMode") || "gallery");
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    localStorage.setItem("docs.viewMode", viewMode);
  }, [viewMode]);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState("file");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    titulo: "", descripcion: "", categoria_id: "", fecha: new Date().toISOString().split("T")[0], url: ""
  });
  const [uploading, setUploading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState({ titulo: "", descripcion: "", categoria_id: "", fecha: "" });

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);

  // Category dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", color: COLOR_PALETTE[0], description: "" });

  // Manage categories
  const [manageCatsOpen, setManageCatsOpen] = useState(false);

  // Batch upload progress (drag&drop multi-file)
  // Each entry: { name, size, status: 'pending'|'uploading'|'done'|'error', error?: string }
  const [batchItems, setBatchItems] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);

  // Delete confirmations
  const [deleteDocConfirm, setDeleteDocConfirm] = useState(null);
  const [deleteCatConfirm, setDeleteCatConfirm] = useState(null);
  const [deleteCatMoveTo, setDeleteCatMoveTo] = useState("__none__");

  useEffect(() => {
    Promise.all([fetchDocuments(), fetchCategories(), fetchStats()]).finally(() => setLoading(false));
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await api.get("/documents");
      setDocuments(res.data);
    } catch { toast.error("Error al cargar documentos"); }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/document-categories");
      setCategories(res.data);
    } catch { toast.error("Error al cargar categorías"); }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get("/documents/stats");
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const refreshAll = () => Promise.all([fetchDocuments(), fetchCategories(), fetchStats()]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      // Multi-select via the file picker → use the same batch flow as drag&drop
      processIncomingFiles(files);
      e.target.value = ""; // reset so the same selection can be retried
      setUploadOpen(false);
      return;
    }
    const file = files[0];
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 10MB.");
      return;
    }
    setUploadFile(file);
    if (!uploadForm.titulo) {
      setUploadForm(f => ({ ...f, titulo: file.name.replace(/\.[^/.]+$/, "") }));
    }
  };

  // Decide how to treat incoming files: single file → open the dialog
  // pre-filled (so the user can pick category/title); 2+ files → fire the
  // batch upload flow with a progress modal.
  const processIncomingFiles = (files) => {
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`"${f.name}" supera los 10MB y fue omitido.`);
        return false;
      }
      return true;
    });
    if (valid.length === 0) return;

    if (valid.length === 1) {
      const file = valid[0];
      setUploadType("file");
      setUploadFile(file);
      setUploadForm({
        titulo: file.name.replace(/\.[^/.]+$/, ""),
        descripcion: "",
        categoria_id: filterCategory !== "__all__" ? filterCategory : "",
        fecha: new Date().toISOString().split("T")[0],
        url: "",
      });
      setUploadOpen(true);
      return;
    }

    // Multi-file: kick off the batch with current category filter as target
    const targetCat = filterCategory !== "__all__" && filterCategory !== "__none__" ? filterCategory : "";
    runBatchUpload(valid, targetCat);
  };

  // Parallel upload (cap concurrency at 3 to avoid hammering the API).
  // Updates batchItems status per file so the modal shows live progress.
  const runBatchUpload = async (files, categoria_id) => {
    setBatchItems(files.map(f => ({ name: f.name, size: f.size, status: "pending" })));
    setBatchOpen(true);
    setBatchRunning(true);

    const CONCURRENCY = 3;
    let cursor = 0;

    const uploadOne = async (idx) => {
      const file = files[idx];
      setBatchItems(items => items.map((it, i) => i === idx ? { ...it, status: "uploading" } : it));
      try {
        const fd = new FormData();
        fd.append("titulo", file.name.replace(/\.[^/.]+$/, ""));
        fd.append("descripcion", "");
        fd.append("categoria_id", categoria_id || "");
        fd.append("fecha", new Date().toISOString().split("T")[0]);
        fd.append("tipo", "file");
        fd.append("url", "");
        fd.append("file", file);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setBatchItems(items => items.map((it, i) => i === idx ? { ...it, status: "done" } : it));
      } catch (err) {
        const msg = err.response?.data?.detail || "Error al subir";
        setBatchItems(items => items.map((it, i) => i === idx ? { ...it, status: "error", error: msg } : it));
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, files.length) }, async () => {
      while (cursor < files.length) {
        const myIdx = cursor++;
        await uploadOne(myIdx);
      }
    });
    await Promise.all(workers);

    setBatchRunning(false);
    await refreshAll();
  };

  // Drag & drop entry-point — supports multiple files. Single file opens
  // the upload dialog pre-filled; 2+ files fire the batch flow with a
  // progress modal.
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    processIncomingFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (uploadType === "file" && !uploadFile) {
      toast.error("Selecciona un archivo");
      return;
    }
    if (uploadType === "link" && !uploadForm.url) {
      toast.error("Ingresa la URL del enlace");
      return;
    }
    if (!uploadForm.titulo) {
      toast.error("El título es requerido");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("titulo", uploadForm.titulo);
      fd.append("descripcion", uploadForm.descripcion);
      fd.append("categoria_id", uploadForm.categoria_id);
      fd.append("fecha", uploadForm.fecha);
      fd.append("tipo", uploadType);
      fd.append("url", uploadForm.url);
      if (uploadType === "file" && uploadFile) fd.append("file", uploadFile);

      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadForm({ titulo: "", descripcion: "", categoria_id: "", fecha: new Date().toISOString().split("T")[0], url: "" });
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    if (doc.tipo === "link") {
      window.open(doc.url, "_blank");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.original_filename || doc.titulo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo. Inténtalo nuevamente.");
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      titulo: doc.titulo,
      descripcion: doc.descripcion || "",
      categoria_id: doc.categoria_id || "",
      fecha: doc.fecha || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("titulo", editForm.titulo);
      fd.append("descripcion", editForm.descripcion);
      fd.append("categoria_id", editForm.categoria_id);
      fd.append("fecha", editForm.fecha);
      await api.put(`/documents/${editingDoc.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento actualizado");
      setEditOpen(false);
      refreshAll();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const confirmDeleteDoc = async () => {
    if (!deleteDocConfirm) return;
    try {
      await api.delete(`/documents/${deleteDocConfirm.id}`);
      toast.success("Documento eliminado");
      setDeleteDocConfirm(null);
      refreshAll();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  // Category management
  const openNewCategory = () => {
    setEditingCat(null);
    setCatForm({ name: "", color: COLOR_PALETTE[0], description: "" });
    setCatDialogOpen(true);
  };

  const openEditCategory = (cat) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, color: cat.color, description: cat.description || "" });
    setCatDialogOpen(true);
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("name", catForm.name);
      fd.append("color", catForm.color);
      fd.append("description", catForm.description);
      if (editingCat) {
        await api.put(`/document-categories/${editingCat.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Categoría actualizada");
      } else {
        await api.post("/document-categories", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Categoría creada");
      }
      setCatDialogOpen(false);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar categoría");
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCatConfirm) return;
    try {
      const moveTo = deleteCatMoveTo && deleteCatMoveTo !== "__none__" ? `?move_to=${deleteCatMoveTo}` : "";
      await api.delete(`/document-categories/${deleteCatConfirm.id}${moveTo}`);
      toast.success("Categoría eliminada");
      setDeleteCatConfirm(null);
      setDeleteCatMoveTo("__none__");
      refreshAll();
    } catch {
      toast.error("Error al eliminar categoría");
    }
  };

  // Filtered + sorted docs
  const visibleDocs = useMemo(() => {
    let arr = [...documents];
    if (filterCategory && filterCategory !== "__all__" && filterCategory !== "__none__") {
      arr = arr.filter(d => d.categoria_id === filterCategory);
    } else if (filterCategory === "__none__") {
      arr = arr.filter(d => !d.categoria_id);
    }
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      arr = arr.filter(d =>
        (d.titulo || "").toLowerCase().includes(term) ||
        (d.descripcion || "").toLowerCase().includes(term) ||
        (d.categoria_name || "").toLowerCase().includes(term) ||
        (d.original_filename || "").toLowerCase().includes(term)
      );
    }
    if (sortBy === "newest") arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortBy === "oldest") arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sortBy === "name") arr.sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""));
    else if (sortBy === "category") arr.sort((a, b) => (a.categoria_name || "").localeCompare(b.categoria_name || ""));
    return arr;
  }, [documents, searchTerm, filterCategory, sortBy]);

  // Document count per category — used by the sidebar labels.
  const docsByCategory = useMemo(() => {
    const map = { __all__: documents.length, __none__: 0 };
    documents.forEach((d) => {
      if (!d.categoria_id) map.__none__ += 1;
      else map[d.categoria_id] = (map[d.categoria_id] || 0) + 1;
    });
    return map;
  }, [documents]);

  if (loading) return <PageLoader label="Cargando documentos…" testid="documents-loader" />;

  return (
    <div data-testid="documents-page">
      <div className="mb-4 flex flex-col gap-1">
        <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Documentos del Negocio
        </h2>
        <p className="text-slate-500 text-sm">
          Almacena, organiza y accede rápidamente a tus documentos importantes
        </p>
      </div>

      {/* Hero Dropzone — primary upload entrypoint, always visible at the top.
         Click → opens native file picker (multi-select enabled).
         Drag&drop → 1 file opens dialog, 2+ files run batch upload with progress. */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`mb-6 rounded-sm border-2 border-dashed transition-all ${
          dragActive
            ? "border-[rgb(0,47,167)] bg-[rgb(0,47,167)]/10 scale-[1.005]"
            : "border-slate-300 bg-gradient-to-br from-slate-50 to-white hover:border-[rgb(0,47,167)] hover:bg-[rgb(0,47,167)]/5"
        }`}
        data-testid="documents-hero-dropzone"
      >
        <label
          htmlFor="hero-file-input"
          className="block cursor-pointer px-5 py-6 sm:py-7"
        >
          <input
            id="hero-file-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              if (files.length > 0) processIncomingFiles(files);
            }}
            className="hidden"
            data-testid="hero-file-input"
          />
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
            <div className={`h-14 w-14 rounded-sm flex items-center justify-center flex-shrink-0 transition-colors ${
              dragActive
                ? "bg-[rgb(0,47,167)] text-white"
                : "bg-white border border-slate-200 text-[rgb(0,47,167)]"
            }`}>
              <Upload className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p className="text-base sm:text-lg font-bold text-slate-900">
                {dragActive ? "Soltá los archivos para subirlos" : "Arrastrá archivos aquí o hacé clic para seleccionar"}
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 flex flex-wrap justify-center sm:justify-start items-center gap-x-1.5 gap-y-0.5">
                <Sparkles className="h-3 w-3 text-[rgb(0,47,167)]" />
                <span>Múltiples archivos a la vez · PDF, Word, Excel, CSV e imágenes · hasta 10MB por archivo</span>
              </p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[rgb(0,47,167)] px-3 py-2 rounded-sm whitespace-nowrap">
              Subir ahora
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </label>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">ARCHIVOS</p>
              <p className="text-2xl font-black">{stats?.total_files || 0}</p>
            </div>
            <FileText className="h-6 w-6 text-[rgb(0,47,167)]" />
          </div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">CATEGORÍAS</p>
              <p className="text-2xl font-black">{stats?.total_categories || 0}</p>
            </div>
            <Folder className="h-6 w-6 text-[rgb(0,47,167)]" />
          </div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">ESPACIO</p>
              <p className="text-2xl font-black">{formatSize(stats?.total_size || 0)}</p>
            </div>
            <HardDrive className="h-6 w-6 text-[rgb(0,47,167)]" />
          </div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">ÚLTIMA SUBIDA</p>
            {stats?.most_recent ? (
              <>
                <p className="text-sm font-semibold truncate">{stats.most_recent.titulo}</p>
                <p className="text-xs text-slate-500">{formatDate(stats.most_recent.created_at)}</p>
              </>
            ) : (
              <p className="text-sm text-slate-500">Sin archivos</p>
            )}
          </div>
        </Card>
      </div>

      {/* Toolbar (search + actions) */}
      <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="document-search-input"
              placeholder="Buscar por nombre, categoría o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-sm pl-9"
            />
          </div>
          {/* Mobile category dropdown (sidebar is hidden on mobile) */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="rounded-sm lg:hidden" data-testid="filter-category-select">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas las categorías ({docsByCategory.__all__})</SelectItem>
              <SelectItem value="__none__">Sin categoría ({docsByCategory.__none__})</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                    {c.name} ({docsByCategory[c.id] || 0})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm lg:w-44" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Más recientes</SelectItem>
              <SelectItem value="oldest">Más antiguos</SelectItem>
              <SelectItem value="name">Nombre A-Z</SelectItem>
              <SelectItem value="category">Categoría</SelectItem>
            </SelectContent>
          </Select>
          {/* View toggle: gallery vs list */}
          <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("gallery")}
              className={`px-2.5 py-2 transition-colors ${viewMode === "gallery" ? "bg-[rgb(0,47,167)] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Vista galería"
              data-testid="view-mode-gallery"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-2.5 py-2 transition-colors border-l border-slate-200 ${viewMode === "table" ? "bg-[rgb(0,47,167)] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Vista lista"
              data-testid="view-mode-table"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Button onClick={() => setManageCatsOpen(true)} variant="outline" className="rounded-sm" data-testid="manage-categories-button">
            <Tag className="h-4 w-4 mr-2" />Categorías
          </Button>
        </div>
      </Card>

      {/* Main grid: sidebar (left) + content (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
        {/* Sidebar (desktop only) */}
        <aside className="hidden lg:block" data-testid="category-sidebar">
          <div className="bg-white border border-slate-200 rounded-sm p-3 sticky top-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-2 pt-1 pb-2">
              Categorías
            </div>
            <nav className="space-y-0.5">
              <SidebarLabel
                active={filterCategory === "__all__"}
                onClick={() => setFilterCategory("__all__")}
                icon={Inbox}
                color="#002FA7"
                label="Todos"
                count={docsByCategory.__all__}
                testid="sidebar-cat-all"
              />
              {categories.map((c) => (
                <SidebarLabel
                  key={c.id}
                  active={filterCategory === c.id}
                  onClick={() => setFilterCategory(c.id)}
                  color={c.color}
                  label={c.name}
                  count={docsByCategory[c.id] || 0}
                  testid={`sidebar-cat-${c.id}`}
                  onEdit={() => openEditCategory(c)}
                  onDelete={() => setDeleteCatConfirm(c)}
                />
              ))}
              {docsByCategory.__none__ > 0 && (
                <SidebarLabel
                  active={filterCategory === "__none__"}
                  onClick={() => setFilterCategory("__none__")}
                  color="#94A3B8"
                  label="Sin categoría"
                  count={docsByCategory.__none__}
                  testid="sidebar-cat-none"
                />
              )}
            </nav>
            <button
              type="button"
              onClick={openNewCategory}
              className="w-full mt-3 text-xs text-[rgb(0,47,167)] flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-[rgb(0,47,167)]/8 font-medium"
              data-testid="sidebar-new-category"
            >
              <Plus className="h-3 w-3" />
              Nueva categoría
            </button>
            <button
              type="button"
              onClick={() => setManageCatsOpen(true)}
              className="w-full text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-slate-50"
            >
              <Tag className="h-3 w-3" />
              Administrar todas
            </button>
          </div>
        </aside>

        {/* Main content area (drag&drop is handled by the hero dropzone above) */}
        <div className="relative">
          {/* Empty state */}
          {documents.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Aún no tenés documentos"
              description="Arrastrá archivos a la zona de arriba o hacé clic en ella para subir tus primeros contratos, facturas o cualquier archivo importante."
              actionLabel="Elegir archivos"
              actionOnClick={() => document.getElementById("hero-file-input")?.click()}
              testid="documents-empty-state"
            />
          ) : visibleDocs.length === 0 ? (
            <EmptyState
              icon={Search}
              title="Sin resultados"
              description="Probá con otra búsqueda o cambia el filtro de categoría."
              testid="documents-no-results"
            />
          ) : viewMode === "gallery" ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
              data-testid="documents-gallery"
            >
              {visibleDocs.map((doc) => {
                const meta = getFileMeta(doc);
                const Icon = meta.Icon;
                const ext = doc.tipo === "link" ? "LINK" : (getFileExt(doc.original_filename) || "").toUpperCase();
                const showImage = isImageDoc(doc);
                const showPdfThumb = isPdfDoc(doc);
                return (
                  <Card
                    key={doc.id}
                    onClick={() => { setDetailDoc(doc); setDetailOpen(true); }}
                    className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group flex flex-col"
                    data-testid="document-card"
                  >
                    {/* Thumbnail zone — image documents use /download, PDFs
                       use /thumbnail (server-rendered first page). Both fall
                       back to the colored ext icon on error. */}
                    <DocCardThumb
                      doc={doc}
                      showImage={showImage}
                      showPdfThumb={showPdfThumb}
                      Icon={Icon}
                      meta={meta}
                      ext={ext}
                    />
                    {/* Info zone */}
                    <div className="p-2.5 flex-1 flex flex-col">
                      <p className="font-medium text-sm text-slate-900 line-clamp-2 leading-snug">{doc.titulo}</p>
                      <p className="text-[10px] text-slate-500 mt-1 truncate">
                        {doc.categoria_name || "Sin categoría"} · {formatDate(doc.created_at)}
                      </p>
                      <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                          className="flex-1 text-xs py-1 rounded-sm bg-[rgb(0,47,167)] text-white hover:bg-[rgb(0,40,150)] flex items-center justify-center gap-1"
                          data-testid={`gallery-download-${doc.id}`}
                        >
                          <Download className="h-3 w-3" />
                          {doc.tipo === "link" ? "Abrir" : "Bajar"}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(doc); }}
                          className="text-xs p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50"
                        >
                          <Edit className="h-3 w-3 text-slate-500" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteDocConfirm(doc); }}
                          className="text-xs p-1.5 rounded-sm border border-slate-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden hidden md:block">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Archivo</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Categoría</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Subido</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Tipo</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Tamaño</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleDocs.map(doc => {
                        const meta = getFileMeta(doc);
                        const Icon = meta.Icon;
                        const ext = doc.tipo === "link" ? "LINK" : getFileExt(doc.original_filename).toUpperCase();
                        return (
                          <tr key={doc.id} className="table-row border-b border-slate-100 cursor-pointer" data-testid="document-row" onClick={() => { setDetailDoc(doc); setDetailOpen(true); }}>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className={`h-8 w-8 rounded-sm flex items-center justify-center ${meta.bg}`}>
                                  <Icon className={`h-4 w-4 ${meta.text}`} />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium truncate">{doc.titulo}</p>
                                  {doc.original_filename && (
                                    <p className="text-xs text-slate-500 truncate">{doc.original_filename}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium" style={{ background: `${doc.categoria_color}20`, color: doc.categoria_color }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: doc.categoria_color }} />
                                {doc.categoria_name || "Sin categoría"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{formatDate(doc.created_at)}</td>
                            <td className="py-3 px-4 mono text-xs text-slate-600">{ext}</td>
                            <td className="py-3 px-4 text-right text-slate-600">{doc.tipo === "file" ? formatSize(doc.size) : "-"}</td>
                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">
                                <Button onClick={() => handleDownload(doc)} size="sm" className="btn-primary rounded-sm h-8" data-testid={`download-${doc.id}`}>
                                  <Download className="h-3 w-3 mr-1" />Descargar
                                </Button>
                                <Button onClick={() => handleEdit(doc)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0" data-testid={`edit-document-${doc.id}`}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button onClick={() => setDeleteDocConfirm(doc)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-document-${doc.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Mobile cards (table mode) */}
              <div className="md:hidden space-y-3">
                {visibleDocs.map(doc => {
                  const meta = getFileMeta(doc);
                  const Icon = meta.Icon;
                  return (
                    <Card key={doc.id} className="bg-white border border-slate-200 rounded-sm p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`h-10 w-10 rounded-sm flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                          <Icon className={`h-5 w-5 ${meta.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{doc.titulo}</p>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-xs font-medium mt-1" style={{ background: `${doc.categoria_color}20`, color: doc.categoria_color }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: doc.categoria_color }} />
                            {doc.categoria_name || "Sin categoría"}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">{formatDate(doc.created_at)} · {doc.tipo === "file" ? formatSize(doc.size) : "Link"}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleDownload(doc)} size="sm" className="btn-primary rounded-sm flex-1">
                          <Download className="h-3 w-3 mr-1" />Descargar
                        </Button>
                        <Button onClick={() => handleEdit(doc)} variant="outline" size="sm" className="rounded-sm">
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button onClick={() => setDeleteDocConfirm(doc)} variant="outline" size="sm" className="rounded-sm text-red-600">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>


      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Subir Documento</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-2">
            <div className="flex gap-2">
              <Button type="button" onClick={() => setUploadType("file")} variant={uploadType === "file" ? "default" : "outline"} className={`flex-1 rounded-sm ${uploadType === "file" ? "btn-primary" : ""}`} data-testid="upload-type-file">
                Archivo
              </Button>
              <Button type="button" onClick={() => setUploadType("link")} variant={uploadType === "link" ? "default" : "outline"} className={`flex-1 rounded-sm ${uploadType === "link" ? "btn-primary" : ""}`} data-testid="upload-type-link">
                Enlace
              </Button>
            </div>

            {uploadType === "file" ? (
              <div className="ai-dropzone p-6 rounded-sm text-center cursor-pointer" onClick={() => document.getElementById("file-input").click()}>
                <input id="file-input" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
                <Upload className="h-8 w-8 mx-auto mb-2 text-[rgb(0,47,167)]" />
                <p className="text-sm font-medium">{uploadFile ? uploadFile.name : "Selecciona un archivo"}</p>
                <p className="text-xs text-slate-500 mt-1">PDF, Word, Excel, imágenes, TXT · Máx 10MB</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1.5">URL del enlace *</label>
                <Input value={uploadForm.url} onChange={(e) => setUploadForm({ ...uploadForm, url: e.target.value })} placeholder="https://..." className="rounded-sm" data-testid="link-url-input" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Título *</label>
              <Input value={uploadForm.titulo} onChange={(e) => setUploadForm({ ...uploadForm, titulo: e.target.value })} required className="rounded-sm" data-testid="document-title-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Categoría</label>
                <Select value={uploadForm.categoria_id || "__none__"} onValueChange={(v) => setUploadForm({ ...uploadForm, categoria_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm" data-testid="document-category-select">
                    <SelectValue placeholder="Sin categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin categoría</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Fecha</label>
                <Input type="date" value={uploadForm.fecha} onChange={(e) => setUploadForm({ ...uploadForm, fecha: e.target.value })} className="rounded-sm" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Descripción / Notas</label>
              <Textarea value={uploadForm.descripcion} onChange={(e) => setUploadForm({ ...uploadForm, descripcion: e.target.value })} rows={2} className="rounded-sm" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" disabled={uploading} className="btn-primary rounded-sm" data-testid="save-document-button">
                {uploading ? "Subiendo..." : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar Documento</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1.5">Título *</label>
              <Input value={editForm.titulo} onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })} required className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Categoría</label>
                <Select value={editForm.categoria_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, categoria_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin categoría</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Fecha</label>
                <Input type="date" value={editForm.fecha} onChange={(e) => setEditForm({ ...editForm, fecha: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Descripción</label>
              <Textarea value={editForm.descripcion} onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })} rows={2} className="rounded-sm" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm">Actualizar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2 pr-8">
              {detailDoc && (() => { const m = getFileMeta(detailDoc); const Icon = m.Icon; return (
                <span className={`h-7 w-7 rounded-sm flex items-center justify-center ${m.bg}`}>
                  <Icon className={`h-4 w-4 ${m.text}`} />
                </span>
              ); })()}
              <span className="truncate">{detailDoc?.titulo}</span>
            </DialogTitle>
          </DialogHeader>
          {detailDoc && (
            <div className="px-5 pb-5">
              {/* Preview pane — image, PDF inline, or "no preview" placeholder */}
              <DocumentPreview doc={detailDoc} />

              <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Categoría</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium" style={{ background: `${detailDoc.categoria_color}20`, color: detailDoc.categoria_color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: detailDoc.categoria_color }} />
                    {detailDoc.categoria_name || "Sin categoría"}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Tipo</p>
                  <p className="font-medium">{detailDoc.tipo === "link" ? "Enlace" : (getFileExt(detailDoc.original_filename).toUpperCase() || "Archivo")}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Subido</p>
                  <p className="font-medium">{formatDateTime(detailDoc.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Tamaño</p>
                  <p className="font-medium">{detailDoc.tipo === "file" ? formatSize(detailDoc.size) : "-"}</p>
                </div>
                {detailDoc.descripcion && (
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Descripción</p>
                    <p className="text-slate-700">{detailDoc.descripcion}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200">
                <Button onClick={() => { setDeleteDocConfirm(detailDoc); setDetailOpen(false); }} variant="outline" className="rounded-sm text-red-600 border-red-200">
                  <Trash2 className="h-3 w-3 mr-1" />Eliminar
                </Button>
                <Button onClick={() => handleDownload(detailDoc)} className="btn-primary rounded-sm">
                  <Download className="h-3 w-3 mr-1" />Descargar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={manageCatsOpen} onOpenChange={setManageCatsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Gestionar Categorías</DialogTitle></DialogHeader>
          <div className="mt-2">
            <Button onClick={openNewCategory} className="btn-primary rounded-sm w-full mb-3" data-testid="new-category-button">
              <Plus className="h-4 w-4 mr-2" />Nueva Categoría
            </Button>
            {categories.length === 0 ? (
              <p className="text-center py-6 text-slate-500 text-sm">Crea categorías para organizar mejor tus documentos.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {categories.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: c.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{c.name}</p>
                      {c.description && <p className="text-xs text-slate-500 truncate">{c.description}</p>}
                    </div>
                    <Button onClick={() => openEditCategory(c)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0">
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button onClick={() => setDeleteCatConfirm(c)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Form Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCat ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle></DialogHeader>
          <form onSubmit={submitCategory} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1.5">Nombre *</label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required className="rounded-sm" data-testid="category-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_PALETTE.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setCatForm({ ...catForm, color })}
                    className={`w-8 h-8 rounded-full transition-all ${catForm.color === color ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                    style={{ background: color }}
                    data-testid={`color-${color.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Descripción (opcional)</label>
              <Input value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} className="rounded-sm" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCatDialogOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm" data-testid="save-category-button">
                {editingCat ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!deleteDocConfirm} onOpenChange={(o) => !o && setDeleteDocConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar "{deleteDocConfirm?.titulo}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDoc} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCatConfirm} onOpenChange={(o) => !o && setDeleteCatConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría "{deleteCatConfirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Qué deseas hacer con los archivos de esta categoría?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-3">
            <Select value={deleteCatMoveTo} onValueChange={setDeleteCatMoveTo}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Mover a "Sin categoría"</SelectItem>
                {categories.filter(c => c.id !== deleteCatConfirm?.id).map(c => (
                  <SelectItem key={c.id} value={c.id}>Mover a "{c.name}"</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCategory} className="bg-red-600 hover:bg-red-700 text-white">Eliminar Categoría</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch upload progress dialog — shown when 2+ files are dropped/picked.
         Lists each file with live status (pending/uploading/done/error). */}
      <Dialog open={batchOpen} onOpenChange={(o) => { if (!batchRunning) setBatchOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {batchRunning ? "Subiendo archivos…" : "Subida completada"}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const total = batchItems.length;
            const done = batchItems.filter(i => i.status === "done").length;
            const errored = batchItems.filter(i => i.status === "error").length;
            const pct = total === 0 ? 0 : Math.round(((done + errored) / total) * 100);
            return (
              <>
                <div className="mt-1 mb-3">
                  <div className="flex justify-between text-xs text-slate-600 mb-1.5">
                    <span data-testid="batch-progress-label">{done + errored} de {total}{errored > 0 ? ` · ${errored} con error` : ""}</span>
                    <span className="font-mono">{pct}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-[rgb(0,47,167)] transition-all"
                      style={{ width: `${pct}%` }}
                      data-testid="batch-progress-bar"
                    />
                  </div>
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="batch-list">
                  {batchItems.map((it, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm px-2.5 py-1.5 border border-slate-100 rounded-sm bg-slate-50/50"
                      data-testid={`batch-item-${idx}`}
                    >
                      {it.status === "uploading" && (
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-[rgb(0,47,167)] border-t-transparent animate-spin flex-shrink-0" />
                      )}
                      {it.status === "done" && (
                        <span className="h-3.5 w-3.5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 text-[9px] font-bold">✓</span>
                      )}
                      {it.status === "error" && (
                        <span className="h-3.5 w-3.5 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0 text-[9px] font-bold">!</span>
                      )}
                      {it.status === "pending" && (
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate font-medium text-slate-800">{it.name}</span>
                      <span className="text-[11px] text-slate-500 font-mono">{formatSize(it.size)}</span>
                      {it.error && (
                        <span className="text-[11px] text-red-600 truncate max-w-[140px]" title={it.error}>{it.error}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-3 pt-3 mt-3 border-t border-slate-200">
                  <Button
                    type="button"
                    disabled={batchRunning}
                    onClick={() => setBatchOpen(false)}
                    className="btn-primary rounded-sm"
                    data-testid="batch-close-button"
                  >
                    {batchRunning ? "Subiendo…" : "Cerrar"}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Document gallery thumbnail. Renders:
//  - Image documents: the full image via /download
//  - PDF documents:   the first-page JPEG thumbnail via /thumbnail
//  - Anything else:   the file-type icon on the colored bg
// Top-left ext badge + top-right category dot are layered on every variant
// so users can scan file format + category at a glance.
function DocCardThumb({ doc, showImage, showPdfThumb, Icon, meta, ext }) {
  const [imgError, setImgError] = useState(false);
  const useImg = (showImage || showPdfThumb) && !imgError;
  const src = showImage ? getDownloadUrl(doc) : (showPdfThumb ? getThumbnailUrl(doc) : null);

  return (
    <div className={`aspect-[4/3] flex items-center justify-center relative overflow-hidden ${useImg ? "bg-slate-100" : meta.bg}`}>
      {useImg ? (
        <img
          src={src}
          alt={doc.titulo}
          loading="lazy"
          className={`w-full h-full ${showPdfThumb ? "object-cover object-top" : "object-cover"}`}
          onError={() => setImgError(true)}
          data-testid={showPdfThumb ? "doc-card-pdf-thumb" : "doc-card-image-thumb"}
        />
      ) : (
        <Icon className={`h-12 w-12 ${meta.text}`} />
      )}
      <span className={`absolute top-1.5 left-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white/95 ${meta.text} shadow-sm`}>
        {ext || "FILE"}
      </span>
      {doc.categoria_color && (
        <span
          className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white"
          style={{ background: doc.categoria_color }}
          title={doc.categoria_name}
        />
      )}
    </div>
  );
}

// Reusable sidebar entry for category navigation.
function SidebarLabel({ active, onClick, icon: ExtraIcon, color, label, count, testid, onEdit, onDelete }) {
  const stop = (e, fn) => { e.stopPropagation(); fn?.(); };
  return (
    <div
      className={`group relative w-full flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors text-sm cursor-pointer ${
        active
          ? "bg-[rgb(0,47,167)]/8 text-[rgb(0,47,167)] font-semibold"
          : "text-slate-700 hover:bg-slate-50"
      }`}
      onClick={onClick}
      data-testid={testid}
    >
      {ExtraIcon ? (
        <ExtraIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color }} />
      ) : (
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color || "#94A3B8" }} />
      )}
      <span className="flex-1 truncate">{label}</span>
      {(onEdit || onDelete) && (
        <span className="hidden group-hover:flex items-center gap-0.5">
          {onEdit && (
            <button
              type="button"
              onClick={(e) => stop(e, onEdit)}
              className="p-0.5 rounded-sm hover:bg-white/80 text-slate-500 hover:text-[rgb(0,47,167)]"
              title="Editar categoría"
              data-testid={`${testid}-edit`}
            >
              <Edit className="h-3 w-3" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => stop(e, onDelete)}
              className="p-0.5 rounded-sm hover:bg-white/80 text-slate-500 hover:text-red-600"
              title="Eliminar categoría"
              data-testid={`${testid}-delete`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </span>
      )}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm tabular-nums ${active ? "bg-[rgb(0,47,167)] text-white" : "bg-slate-100 text-slate-600"} ${(onEdit || onDelete) ? "group-hover:hidden" : ""}`}>
        {count}
      </span>
    </div>
  );
}

// Document preview pane — handles 3 cases:
//   1. Images: full-size <img>
//   2. PDFs:   <iframe> with the auth-included download URL (browser renders)
//   3. Other:  big coloured icon + "no preview, please download" call-to-action
function DocumentPreview({ doc }) {
  const meta = getFileMeta(doc);
  const Icon = meta.Icon;
  const ext = getFileExt(doc.original_filename);
  const isImage = doc.tipo === "file" && IMAGE_EXTS.has(ext);
  const isPdf = doc.tipo === "file" && ext === "pdf";
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : "";
  const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`;

  if (doc.tipo === "link") {
    return (
      <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-6 text-center" data-testid="doc-preview-link">
        <Icon className="h-12 w-12 mx-auto mb-2 text-amber-600" />
        <p className="text-sm font-medium text-slate-700">Enlace externo</p>
        {doc.url && (
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[rgb(0,47,167)] underline mt-1 inline-block truncate max-w-full"
          >
            {doc.url}
          </a>
        )}
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50 flex items-center justify-center" data-testid="doc-preview-image">
        <img
          src={previewUrl}
          alt={doc.titulo}
          className="max-h-[60vh] w-auto"
        />
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50" data-testid="doc-preview-pdf">
        <iframe
          src={previewUrl}
          title={doc.titulo}
          className="w-full h-[60vh]"
        />
      </div>
    );
  }

  // No native preview (xlsx, docx, csv, txt, etc.)
  return (
    <div className={`border border-slate-200 rounded-sm p-8 text-center ${meta.bg}`} data-testid="doc-preview-fallback">
      <Icon className={`h-16 w-16 mx-auto mb-3 ${meta.text}`} />
      <p className="text-sm font-medium text-slate-700">
        Vista previa no disponible para este formato
      </p>
      <p className="text-xs text-slate-500 mt-1">
        Descargá el archivo para abrirlo con la app correspondiente.
      </p>
    </div>
  );
}
